import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { user } from '../../../redux/user';
import { showNotification } from '../../../redux/notification';
import utils from '../../../utils/utils';
import ConfirmationModalNew from '../../../components/modals/ConfirmationModalNew';
import IsAuthorizedForManagementCompany from '../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import {
	useLazyGetUpcomingShiftsByProfessionalQuery,
	useUpdateWorkforceStatusMutation
} from '../../../redux/managementCompanyWorkforce';
import { PERMISSION as PERMISSIONS } from '../../../constants';
import manageAccount from '../../../assets/images/manage_accounts.svg';
import { ReactComponent as KebobIcon } from '../../../assets/images/more-horizontal.svg';

const DatatableActionsColumn = ({ managementCompany, caregiver }) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const userData = useSelector(user, shallowEqual);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [newStatus, setNewStatus] = useState('');
	const [upcomingShifts, setUpcomingShifts] = useState([]);
	const [updateWorkforceStatus, { isLoading: loadingUpdate }] = useUpdateWorkforceStatusMutation();
	const [getUpcomingShifts, { isLoading: loadingUpcomingShifts }] = useLazyGetUpcomingShiftsByProfessionalQuery();

	const statusUpdateModalContent = useMemo(() => {
		const upcomingShiftsCount = upcomingShifts ? upcomingShifts.length : 0;
		const { employeeStatus } = caregiver;
		const isCurrentlyEnabled = employeeStatus === 'enabled';
		const showUpcomingText = isCurrentlyEnabled && upcomingShiftsCount > 0;
		const pluralizedShiftText = utils.pluralize('shift', 's', upcomingShiftsCount);

		const modalBody = showUpcomingText ? (
			<div>
				This professional is currently enabled for {managementCompany.name} and has{' '}
				<strong>
					{upcomingShiftsCount} upcoming {pluralizedShiftText}
				</strong>
				. If you set them to {newStatus.toLowerCase()}, they will be removed from the {pluralizedShiftText},{' '}
				{upcomingShiftsCount === 1 ? ' a' : ' '} replacement {pluralizedShiftText} will be added, and they will{' '}
				<strong>no longer</strong> be able to work shifts on your behalf.
			</div>
		) : (
			<div>
				This professional is currently {employeeStatus} for {managementCompany.name}; if you set them to{' '}
				{newStatus.toLowerCase()}, they will{' '}
				{isCurrentlyEnabled ? (
					<span>
						<strong> no longer</strong> be able
					</span>
				) : (
					<span>
						{(newStatus === 'ENABLED' && 'be able') || ''}
						{(newStatus !== 'ENABLED' && <strong>remain unable</strong>) || ''}
					</span>
				)}{' '}
				to work shifts on your behalf.
			</div>
		);

		return {
			title: `Set professional to ${newStatus.toLowerCase()}?`,
			body: modalBody,
			primaryLabel: 'Update status',
			secondaryLabel: `Keep ${employeeStatus}`
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [caregiver, managementCompany, upcomingShifts, newStatus]);

	const handleConfirmationModalShow = async (_newStatus) => {
		setShowConfirmationModal(true);
		setNewStatus(_newStatus);
		if (caregiver.employeeStatus === 'enabled') {
			const { data: upcomingShiftsData } = await getUpcomingShifts({
				managementCompanyId: managementCompany.id,
				caregiverId: caregiver.id
			});
			setUpcomingShifts(upcomingShiftsData);
		}
	};

	const handleStatusChangeAction = async () => {
		try {
			await updateWorkforceStatus({
				managementCompanyId: managementCompany.id,
				caregiverId: caregiver.id,
				body: {
					status: newStatus
				}
			});
			setShowConfirmationModal(false);
		} catch (error) {
			const fallbackMessage = `There was an error updating employee status.`;
			dispatch(showNotification('error', utils.parseRTKQueryValidationError(error, fallbackMessage)));
		}
	};

	if (caregiver.isActive === false) {
		return null;
	}

	return [
		<div className="actions-container" key="actions-container">
			<OverlayTrigger
				placement="bottom"
				overlay={
					<Tooltip className="workforce-action-tooltip" id="manage-profile">
						Manage profile
					</Tooltip>
				}
			>
				<button
					type="button"
					className="btn"
					disabled={
						!utils.isAuthorizedExact(
							PERMISSIONS.WORKFORCE_PROFESSIONAL_VIEW,
							userData.managementCompanyPermissions[managementCompany.id]
						)
					}
					onClick={() =>
						history.push(`/private/workforce/${managementCompany.id}/professionals/${caregiver.id}/employee-details`)
					}
					aria-label="Manage profile"
				>
					<img src={manageAccount} alt="Manage account" />
				</button>
			</OverlayTrigger>
			{caregiver.isActive && (
				<IsAuthorizedForManagementCompany
					exactPermission={PERMISSIONS.WORKFORCE_PROFESSIONAL_EDIT}
					managementCompanyId={managementCompany.id}
					alternativeRender={<div className="action-menu-placeholder" />}
				>
					<div className="action-menu-container">
						<Dropdown>
							<Dropdown.Toggle className="action-menu">
								<KebobIcon alt="Show More" className="action-menu-icon" />
							</Dropdown.Toggle>
							<Dropdown.Menu>
								{caregiver.employeeStatus !== 'enabled' && (
									<Dropdown.Item className="action-menu-item" onClick={() => handleConfirmationModalShow('ENABLED')}>
										Enable
									</Dropdown.Item>
								)}
								{caregiver.employeeStatus !== 'pending' && (
									<Dropdown.Item className="action-menu-item" onClick={() => handleConfirmationModalShow('PENDING')}>
										Pending
									</Dropdown.Item>
								)}
								{caregiver.employeeStatus !== 'disabled' && (
									<Dropdown.Item
										className="action-menu-item action-menu-item__warning"
										onClick={() => handleConfirmationModalShow('DISABLED')}
									>
										Disable
									</Dropdown.Item>
								)}
								{caregiver.employeeStatus !== 'terminated' && (
									<Dropdown.Item
										className="action-menu-item action-menu-item__warning"
										onClick={() => handleConfirmationModalShow('TERMINATED')}
									>
										Terminate
									</Dropdown.Item>
								)}
							</Dropdown.Menu>
						</Dropdown>
					</div>
				</IsAuthorizedForManagementCompany>
			)}
		</div>,

		showConfirmationModal && (
			<ConfirmationModalNew
				key="workforce-confirmation-modal"
				size="l"
				className={classNames('workforce-confirmation-modal', { 'is-loading': loadingUpdate || loadingUpcomingShifts })}
				cancelLabel={statusUpdateModalContent.secondaryLabel}
				title={statusUpdateModalContent.title}
				onCancel={() => setShowConfirmationModal(false)}
				onConfirm={handleStatusChangeAction}
				confirmLabel={statusUpdateModalContent.primaryLabel}
			>
				<>{loadingUpdate || loadingUpcomingShifts ? <LoadingSpinner /> : statusUpdateModalContent.body}</>
			</ConfirmationModalNew>
		)
	];
};

export default DatatableActionsColumn;
